
import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaArrowLeft } from 'react-icons/fa';
import { verifyResetToken, resetPassword } from '../services/authService';

const ResetPasswordPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loading, setLoading] = useState(false);
    const [tokenLoading, setTokenLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Token doğrulama
    useEffect(() => {
        const checkToken = async () => {
            try {
                setTokenLoading(true);
                const result = await verifyResetToken(token);

                if (result.success) {
                    setTokenValid(true);
                } else {
                    setError('Geçersiz veya süresi dolmuş bağlantı');
                }
            } catch (error) {
                setError('Bağlantı geçersiz veya süresi dolmuş');
            } finally {
                setTokenLoading(false);
            }
        };

        if (token) {
            checkToken();
        } else {
            setError('Geçersiz bağlantı');
            setTokenLoading(false);
        }
    }, [token]);

    // Şifre kuvveti kontrolü
    const getPasswordStrength = () => {
        const minLength = password.length >= 8;
        const hasUpper = /[A-Z]/.test(password);
        const hasLower = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);

        const score = [minLength, hasUpper, hasLower, hasNumber].filter(Boolean).length;

        if (score === 4) return { level: 'Güçlü', color: 'success', width: '100%' };
        if (score === 3) return { level: 'Orta', color: 'warning', width: '75%' };
        if (score === 2) return { level: 'Zayıf', color: 'danger', width: '50%' };
        return { level: 'Çok Zayıf', color: 'danger', width: '25%' };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Şifre kontrolü
        if (password.length < 8) {
            setError('Şifre en az 8 karakter olmalıdır');
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError('Şifreler eşleşmiyor');
            setLoading(false);
            return;
        }

        try {
            const result = await resetPassword(token, password);

            if (result.success) {
                setSuccess(true);
            } else {
                setError(result.message || 'Şifre sıfırlama başarısız');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Sunucu hatası oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Token yükleniyor
    if (tokenLoading) {
        return (
            <div className="reset-password-page">
                <section className="py-5">
                    <Container>
                        <div className="text-center">
                            <Spinner animation="border" role="status" style={{ color: 'var(--black)' }}>
                                <span className="visually-hidden">Yükleniyor...</span>
                            </Spinner>
                            <p className="mt-3">Bağlantı doğrulanıyor...</p>
                        </div>
                    </Container>
                </section>
            </div>
        );
    }

    // Başarılı sıfırlama
    if (success) {
        return (
            <div className="reset-password-page">
                <section className="py-5">
                    <Container>
                        <div className="text-center mb-5">
                            <h1 className="section-title">Şifre Başarıyla Değiştirildi</h1>
                        </div>
                    </Container>
                </section>

                <section className="py-5 bg-light">
                    <Container>
                        <Row className="justify-content-center">
                            <Col xs={12} md={6} lg={5}>
                                <div className="modern-faq-item text-center">
                                    <div className="advantage-icon-modern mb-4" style={{ color: 'var(--success-color)' }}>
                                        <FaCheckCircle />
                                    </div>

                                    <h3 className="modern-faq-question mb-4">Şifreniz Başarıyla Güncellendi!</h3>

                                    <div className="modern-faq-answer">
                                        <p className="mb-4">
                                            Yeni şifreniz oluşturuldu. Artık yeni şifrenizle giriş yapabilirsiniz.
                                        </p>
                                    </div>

                                    <div className="mt-4">
                                        <Button
                                            className="apply-btn-black"
                                            onClick={() => navigate('/login')}
                                        >
                                            <FaArrowLeft className="me-2" />
                                            Giriş Sayfasına Git
                                        </Button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        );
    }

    // Token geçersiz
    if (!tokenValid) {
        return (
            <div className="reset-password-page">
                <section className="py-5">
                    <Container>
                        <div className="text-center mb-5">
                            <h1 className="section-title">Geçersiz Bağlantı</h1>
                        </div>
                    </Container>
                </section>

                <section className="py-5 bg-light">
                    <Container>
                        <Row className="justify-content-center">
                            <Col xs={12} md={6} lg={5}>
                                <div className="modern-faq-item text-center">
                                    <Alert variant="danger" className="mb-4">
                                        {error}
                                    </Alert>

                                    <div className="modern-faq-answer">
                                        <p className="mb-4">
                                            Şifre sıfırlama bağlantısı geçersiz veya süresi dolmuş olabilir.
                                        </p>
                                        <p className="mb-4">
                                            Lütfen yeni bir şifre sıfırlama isteği gönderin.
                                        </p>
                                    </div>

                                    <div className="d-flex gap-3 justify-content-center">
                                        <Link to="/forgot-password" className="btn apply-btn-black">
                                            Tekrar Dene
                                        </Link>
                                        <Link to="/login" className="btn btn-outline-dark">
                                            Giriş Sayfası
                                        </Link>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </div>
        );
    }

    const strength = getPasswordStrength();

    return (
        <div className="reset-password-page">
            <section className="py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h1 className="section-title">Yeni Şifre Oluştur</h1>
                        <p className="lead">Güvenli bir şifre seçin</p>
                    </div>
                </Container>
            </section>

            <section className="py-5 bg-light">
                <Container>
                    <Row className="justify-content-center">
                        <Col xs={12} md={6} lg={5}>
                            <div className="modern-faq-item">
                                <div className="text-center mb-4">
                                    <div className="advantage-icon-modern mb-3">
                                        <FaLock />
                                    </div>
                                    <h3 className="modern-faq-question">Yeni Şifrenizi Belirleyin</h3>
                                </div>

                                {error && (
                                    <Alert variant="danger" className="mb-4">
                                        {error}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3" controlId="password">
                                        <Form.Label>
                                            <FaLock className="me-2" />
                                            Yeni Şifre
                                        </Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Yeni şifrenizi girin"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="py-3 pe-5"
                                            />
                                            <Button
                                                variant="link"
                                                className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent"
                                                style={{ zIndex: 10 }}
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </Button>
                                        </div>

                                        {password && (
                                            <div className="mt-2">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <small>Şifre Kuvveti:</small>
                                                    <small className={`text-${strength.color}`}>{strength.level}</small>
                                                </div>
                                                <div className="progress" style={{ height: '4px' }}>
                                                    <div
                                                        className={`progress-bar bg-${strength.color}`}
                                                        style={{ width: strength.width }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-4" controlId="confirmPassword">
                                        <Form.Label>
                                            <FaLock className="me-2" />
                                            Şifre Tekrar
                                        </Form.Label>
                                        <div className="position-relative">
                                            <Form.Control
                                                type={showConfirmPassword ? "text" : "password"}
                                                placeholder="Şifrenizi tekrar girin"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                className="py-3 pe-5"
                                            />
                                            <Button
                                                variant="link"
                                                className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent"
                                                style={{ zIndex: 10 }}
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </Button>
                                        </div>
                                        {confirmPassword && password !== confirmPassword && (
                                            <Form.Text className="text-danger">
                                                Şifreler eşleşmiyor
                                            </Form.Text>
                                        )}
                                    </Form.Group>

                                    <div className="mb-4 p-3 bg-light rounded">
                                        <small className="text-muted">
                                            <strong>Güvenli şifre için:</strong><br />
                                            • En az 8 karakter<br />
                                            • Büyük ve küçük harf<br />
                                            • En az 1 rakam<br />
                                        </small>
                                    </div>

                                    <Button
                                        type="submit"
                                        className="apply-btn-black w-100 py-3 mb-4"
                                        disabled={loading || password !== confirmPassword}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Güncelleniyor...
                                            </>
                                        ) : (
                                            <>
                                                <FaLock className="me-2" />
                                                Şifreyi Güncelle
                                            </>
                                        )}
                                    </Button>

                                    <div className="text-center">
                                        <Link to="/login" className="text-decoration-none" style={{ color: 'var(--black)' }}>
                                            <FaArrowLeft className="me-2" />
                                            Giriş sayfasına dön
                                        </Link>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default ResetPasswordPage;